// @ts-check

import React, { useState } from "react";
import { Card, EmptySearchResult, IndexTable, InlineStack, Spinner, Text, useBreakpoints } from "@shopify/polaris";

import { useGlobalContext } from "context/GlobalContext";

import { UTM_SETS_PAGE_SIZE } from "utils/utm-sets";
import { SavedUtmLinksTableRow } from "./SavedUtmLinksTableRow";

const COLUMNS = [
  {
    label: "UTM Source",
    key: "utm_source",
  },
  {
    label: "UTM Medium",
    key: "utm_medium",
  },
  {
    label: "UTM Campaign",
    key: "utm_campaign",
  },
  {
    label: "UTM Content",
    key: "utm_content",
  },
  {
    label: "UTM Term",
    key: "utm_term",
  },
  {
    label: "Actions",
    key: "actions",
  },
];

export const SavedUtmLinksTable = () => {
  const {
    savedUtmSets,
    isRefetchingSavedUtmSets,
    savedUtmSetsPage,
    setSavedUtmSetsPage,
    savedUtmSetsTotalCount,
  } = useGlobalContext();

  const [columns, setColumns] = useState([
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "actions",
  ]);

  const sortedValues = COLUMNS.map((col) => col.key);

  const headings = COLUMNS
    .map((column) => {
      return {
        id: column.label,
        alignment: column.numeric || column.key === "actions" ? "end" : "start",
        title: column.label,
      };
    });

  const emptyStateMarkup = (
    <EmptySearchResult
      title="No saved UTM sets"
      description="Create on in the right sidebar"
      withIllustration
    />
  );

  const totalPages = ~~(savedUtmSetsTotalCount / UTM_SETS_PAGE_SIZE) + 1;

  const paginationLabel = (
    <InlineStack gap="300" align="center">
      <Text fontWeight="semibold" as="p">Page {savedUtmSetsPage} of {totalPages}</Text>
      {isRefetchingSavedUtmSets && <Spinner size="small" />}
    </InlineStack>
  );

  return (
    <Card padding="0">
      <IndexTable
        condensed={useBreakpoints().smDown}
        resourceName={{ singular: "created UTM link", plural: "created UTM links" }}
        itemCount={savedUtmSets.length}
        selectable={false}
        emptyState={emptyStateMarkup}
        headings={headings}
        lastColumnSticky
        pagination={{
          label: paginationLabel,
          hasPrevious: savedUtmSetsPage > 1,
          hasNext: UTM_SETS_PAGE_SIZE * savedUtmSetsPage < savedUtmSetsTotalCount,
          onNext: () => {
            setSavedUtmSetsPage(page => Math.min(totalPages, page + 1));
          },
          onPrevious: () => {
            setSavedUtmSetsPage(page => Math.max(0, page - 1));
          },
        }}
      >
        {savedUtmSets.map((row, index) => (
          <SavedUtmLinksTableRow
            key={index}
            index={index}
            data={row}
            columns={columns}
            sortedValues={sortedValues}
          />
        ))}
      </IndexTable>
    </Card>
  );
};
